<script setup>
import {computed, onMounted, ref, watch} from "vue";
import {useStore} from "vuex";
import CommentInboxList from "@/components/common/CommentInboxList.vue";
import CommentThreadPanel from "@/components/common/CommentThreadPanel.vue";

const store = useStore();

// Selection lives here so both panes read the same active thread.
const selectedCommentId = ref(null);

const comments = computed(() => store.getters.getComments);

// The card lists the whole project, so a selected model is not required — only a project.
const hasProject = computed(() => store.state.projectData?.projectId != null);

const isLoading = computed(() => store.state.modelViewer.comments.loading);

const error = computed(() => store.state.modelViewer.comments.error);

const dismissError = () => {
  store.commit('setCommentsError', null);
};

const selectedComment = computed(
  () => comments.value.find((comment) => comment.id === selectedCommentId.value) ?? null
);

// `retrieveCommentsReplies` walks every thread, so ask for it at most once per
// selection instead of on every state change while the replies are coming in.
const repliesRequested = new Set();

watch(selectedComment, (comment) => {
  if (!comment || repliesRequested.has(comment.id)) {
    return;
  }
  if (comment.replies.length < comment.getReplyCount()) {
    repliesRequested.add(comment.id);
    store.dispatch('retrieveCommentsReplies');
  }
});

const newCommentOpen = ref(false);
const newComment      = ref({title: null, text: null});
const isCreating      = ref(false);

const openNewComment = () => {
  newComment.value  = {title: null, text: null};
  newCommentOpen.value = true;
};

const createComment = async () => {
  isCreating.value = true;
  try {
    const commentId = await store.dispatch('addNewComment', {
      title: newComment.value.title,
      text : newComment.value.text
    });
    if (commentId) {
      // Open the thread that was just created.
      selectedCommentId.value = commentId;
      newCommentOpen.value    = false;
    }
  } finally {
    isCreating.value = false;
  }
};

const sendReply = (payload) => {
  store.dispatch('addReplyToComment', payload);
};

// The store applies each of these optimistically and rolls back if Speckle rejects it.
const changeStatus = (payload) => {
  store.dispatch('updateCommentStatus', payload);
};

const changePriority = (payload) => {
  store.dispatch('updateCommentPriority', payload);
};

const changeDueDate = (payload) => {
  store.dispatch('updateCommentDueDate', payload);
};

onMounted(() => {
  // Project-wide: general issues created from this card carry no 3D reference, so they
  // match no model filter and would be invisible in a model-scoped list.
  store.dispatch('retrieveComments', {projectWide: true});
});
</script>

<template>
  <v-card class="default-card pa-6" elevation="3">
    <v-card-title class="toolbox-card-title project-comments-title">
      {{ $t('comments.title') }}
      <v-spacer></v-spacer>
      <v-btn color="secondary"
             density="comfortable"
             prepend-icon="mdi-plus"
             :disabled="!hasProject"
             @click="openNewComment()"
      >
        {{ $t('placeholders.newComment') }}
      </v-btn>
    </v-card-title>

    <v-dialog v-model="newCommentOpen" max-width="560">
      <v-card class="pa-4">
        <v-card-title>{{ $t('placeholders.newComment') }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="newComment.title"
                        :label="$t('comments.newComment.title')"
                        variant="outlined"
                        density="comfortable"
                        autofocus
          ></v-text-field>
          <v-textarea v-model="newComment.text"
                      :label="$t('comments.newComment.description')"
                      variant="outlined"
                      density="comfortable"
                      rows="4"
                      auto-grow
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text"
                 :disabled="isCreating"
                 @click="newCommentOpen = false"
          >
            {{ $t('buttons.cancel') }}
          </v-btn>
          <!-- Issues require a title; without one the body's first line becomes the title. -->
          <v-btn color="secondary"
                 variant="flat"
                 :loading="isCreating"
                 :disabled="!newComment.title && !newComment.text"
                 @click="createComment()"
          >
            {{ $t('add') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-alert v-if="error"
             class="project-comments-error"
             type="error"
             variant="tonal"
             density="compact"
             closable
             :title="$t('comments.actionFailed')"
             @click:close="dismissError()"
    >
      {{ error }}
    </v-alert>

    <v-row class="project-comments-panes" no-gutters>
      <v-col cols="12" md="5" class="project-comments-list">
        <comment-inbox-list v-model="selectedCommentId"
                            :comments="comments"
                            :loading="isLoading"
                            :has-project="hasProject"
        ></comment-inbox-list>
      </v-col>

      <v-col cols="12" md="7" class="project-comments-thread">
        <comment-thread-panel :comment="selectedComment"
                              @reply="sendReply"
                              @update:status="changeStatus"
                              @update:priority="changePriority"
                              @update:dueDate="changeDueDate"
        ></comment-thread-panel>
      </v-col>
    </v-row>
  </v-card>
</template>

<style scoped>
@import '@/assets/styles/components/ProjectComments.css';
</style>
